import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import colors from "../../../styles/Colors";

export interface CommentDetail {
  id: string | number;
  comment: string;
  source: string; // "precedente" | "Soporte" | otro
  userName: string;
  createdAt: string; // ISO
  analysisId?: string | number;
  analysisFile?: string;
}

const formatDate = (iso?: string) => {
  if (!iso) return "N/A";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "Fecha inválida";
  }
};

type Props = {
  open: boolean;
  onClose: () => void;
  data: CommentDetail | null;
};

const CommentDetailDialog: React.FC<Props> = ({ open, onClose, data }) => {
  const dateText = formatDate(data?.createdAt);
  const navigate = useNavigate();

  const goToAnalysis = () => {
    if (data?.analysisFile) {
      navigate(`/precedente/${data.analysisFile}`);
    }
  };

  const handleCopy = () => {
    if (data?.comment) navigator.clipboard.writeText(data.comment);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, p: 0, boxShadow: 4 },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontWeight: 500,
          fontSize: 15,
          pb: 0,
        }}
      >
        Ver Comentario
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
          aria-label="Cerrar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        {/* Línea 1: Chip + Ver Análisis + Fecha */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          mb={1.5}
        >
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Chip
               label={data?.source === "precedente" ? "Análisis de Precedentes" : "Campo de soporte"}
              sx={{
                backgroundColor: colors.colorPrimary,
                color: "#fff",
                fontWeight: 600,
                borderRadius: "6px",
                height: 32,
                ".MuiChip-label": { px: 1.5, fontSize: 14 },
              }}
            />
            {data?.source === "precedente" && data?.analysisFile && (
              <Button
                onClick={goToAnalysis}
                size="small"
                variant="text"
                startIcon={<VisibilityOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: 14,
                  color: "#333",
                }}
              >
                Ver Análisis
              </Button>
            )}
          </Box>

          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
            {dateText}
          </Typography>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Línea 2: Usuario / IDs */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          rowGap={0.75}
          mb={1.5}
        >
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Usuario:{" "}
            <Typography
              component="span"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {data?.userName ?? "-"}
            </Typography>
          </Typography>

          <Box display="flex" gap={3}>
            {data?.analysisId && (
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                ID Análisis:{" "}
                <Typography
                  component="span"
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  {data.analysisId}
                </Typography>
              </Typography>
            )}
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              ID Comentario:{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {data?.id ?? "-"}
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* Cuerpo del comentario */}
        <Box
          sx={{
            bgcolor: "#fff",
            p: 0.5,
            position: "relative",
          }}
        >
          <Typography sx={{ fontSize: 14, lineHeight: 1.6, pr: 4 }}>
            {data?.comment ?? "-"}
          </Typography>

          {data?.comment && (
            <Tooltip title="Copiar comentario">
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  color: "text.secondary",
                  "&:hover": { color: "text.primary" },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommentDetailDialog;